import { OverlayViewF } from '@react-google-maps/api'
import React, { MouseEventHandler, useCallback, useContext, useEffect, useRef } from 'react'
import styled from 'styled-components'

import SelectedMapItemPin from './Pins/SelectedMapItemPin'
import { BaseMapElementComponentProps, pinOffSetter } from '../utils'

import Clickable from 'components/Common/Clickable'
import { leOfferSelectionId } from 'tripPlanner/utils/itemSelection'
import { TripMapContext } from 'tripPlanner/contexts/TripMapContext'
import TeardropPin from './Pins/TeardropPin'
import { themeClassName } from 'lib/theme/themeUtils'
import LineTicketIcon from 'components/Luxkit/Icons/line/LineTicketIcon'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import { isExperienceOffer } from 'lib/offer/offerTypes'

const StyledClickable = styled(Clickable)`
  position: absolute;
  left: 0;
  bottom: 0;
  transform: translateX(-50%);
`

interface Props extends BaseMapElementComponentProps {
  offer: App.HotelOffer | App.ExperienceOffer
}

function LEOfferMapMarker({
  active,
  offer,
  position,
  zIndex,
}: Props) {
  const { onSelectMapItem, onDeselectMapItem } = useContext(TripMapContext)

  const Icon = isExperienceOffer(offer) ? LineTicketIcon : LineBedIcon
  const name = isExperienceOffer(offer) ? offer.name : offer.property.name

  const onClick: MouseEventHandler = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (active) {
        onDeselectMapItem()
      } else {
        onSelectMapItem(leOfferSelectionId(offer))
      }
    },
    [active, onDeselectMapItem, onSelectMapItem, offer],
  )

  const ref = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (ref.current && google?.maps) {
      google.maps.OverlayView.preventMapHitsFrom(ref.current)
    }
  }, [ref])

  return (
    <OverlayViewF
      zIndex={active ? 999 : zIndex}
      position={position}
      css={{ pointerEvents: 'none' }}
      getPixelPositionOffset={pinOffSetter}
      mapPaneName="overlayMouseTarget"
    >
      <StyledClickable onClick={onClick} ref={ref}>
        {active && <SelectedMapItemPin text={name} />}
        {!active && (
          <TeardropPin isActive={false} className={themeClassName('inverse')}>
            <Icon colour="neutral-eight" />
          </TeardropPin>
        )}
      </StyledClickable>
    </OverlayViewF>
  )
}

export default React.memo(LEOfferMapMarker)
